import firebase from 'firebase/app';

export const GOOG_RECAPTCHA_SITE_KEY = '6LdZWSYrAAAAAIeT9OanpWKafZysLd_gMis6EgD8';

/** True when reCAPTCHA v3 has loaded on this page, i.e. a lookup can be attempted. */
export function canGetRecaptchaToken() {
  const g = typeof window !== 'undefined' ? window.grecaptcha : null;
  return !!(g && typeof g.execute === 'function');
}

/**
 * True when this page loads reCAPTCHA at all (the api.js script tag), even if the
 * library hasn't finished arriving yet. On a phone returning from a Google/Apple
 * redirect, the sign-in result often lands before grecaptcha.execute exists.
 */
export function recaptchaExpected() {
  if (canGetRecaptchaToken()) return true;
  if (typeof document === 'undefined' || !document.querySelector) return false;
  return !!document.querySelector('script[src*="recaptcha/api.js"]');
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A reCAPTCHA v3 token for `action`, or null when grecaptcha isn't available.
 * Waits up to `waitMs` for the library when the page is expected to load it.
 */
export async function getRecaptchaToken(action, { waitMs = 4000 } = {}) {
  const deadline = Date.now() + waitMs;
  while (!canGetRecaptchaToken() && recaptchaExpected() && Date.now() < deadline) {
    await sleep(100); // eslint-disable-line no-await-in-loop
  }
  if (!canGetRecaptchaToken()) return null;
  const g = window.grecaptcha;
  await new Promise((resolve) => (typeof g.ready === 'function' ? g.ready(resolve) : resolve()));
  return g.execute(GOOG_RECAPTCHA_SITE_KEY, { action });
}

/**
 * Server-side account lookup for an email or an E.164 mobile number — the same
 * call the identity-first field makes. Resolves to
 *   { status: 'has_credential' | 'account_no_credential' | 'not_found', providers, hasPhoneCredential }
 * and throws when reCAPTCHA is unavailable or the call fails, so the caller decides
 * what "couldn't check" means for its screen.
 */
export async function lookupAccountIdentity(identifier) {
  const token = await getRecaptchaToken('account_lookup');
  if (!token) throw new Error('reCAPTCHA unavailable');
  const lookup = firebase.functions().httpsCallable('userService-accountLookupHandler-lookupAccountIdentity');
  const res = await lookup({ identifier, token, action: 'account_lookup' });
  const data = (res && res.data) || {};
  return {
    status: data.status || 'not_found',
    providers: data.providers || [],
    hasPhoneCredential: !!data.hasPhoneCredential,
  };
}

const PROVIDER_IDS = {
  'google.com': 'google', 'apple.com': 'apple', password: 'email', emailLink: 'email', phone: 'phone',
};

/** Normalised sign-in methods of a Firebase Auth user object. */
export function providersOfAuthUser(user) {
  const out = ((user && user.providerData) || [])
    .map((p) => PROVIDER_IDS[p && p.providerId] || null)
    .filter(Boolean);
  if (user && user.phoneNumber) out.push('phone');
  return Array.from(new Set(out));
}

/**
 * Would this lookup result be the signed-in user finding ITSELF? True when it is a
 * credential match on the user's own primary identifier whose methods are all
 * methods this user already has. A different account can't hold the same Google
 * email as a credential, so nothing real is hidden — but a lookup handler that
 * doesn't exclude the caller (deployed a step behind this webapp) would return
 * exactly this for every brand-new person.
 */
export function isSelfMatch(result, user, identifier) {
  if (!result || result.status !== 'has_credential' || !user) return false;
  const own = [user.email, user.phoneNumber].filter(Boolean).map((v) => String(v).toLowerCase());
  if (!own.includes(String(identifier || '').toLowerCase())) return false;
  const mine = providersOfAuthUser(user);
  const theirs = result.providers || [];
  return theirs.length > 0 && theirs.every((p) => mine.includes(p));
}

/**
 * Is there an account OTHER than `user` for its email (else phone)? Resolves to
 * { status, providers, hasPhoneCredential, identifier } or null: no identifier, no
 * match, the user finding itself, a failed lookup, or more than `timeoutMs`.
 * Never throws — a check that can't run must never cost anyone their sign-in.
 */
export async function findExistingAccountFor(user, { timeoutMs = 6000 } = {}) {
  const identifier = (user && (user.email || user.phoneNumber)) || null;
  if (!identifier) return null;
  let timer = null;
  try {
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error('lookup timed out')), timeoutMs);
    });
    const result = await Promise.race([lookupAccountIdentity(identifier), timeout]);
    if (result.status === 'not_found' || !(result.providers || []).length) return null;
    if (isSelfMatch(result, user, identifier)) return null;
    return { ...result, identifier };
  } catch (err) {
    console.warn('[login] existing-account check skipped:', err?.message);
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Human wording for a providers list: ['google', 'phone'] → "Google or your mobile number". */
export function describeProviders(providers) {
  const labels = {
    google: 'Google', apple: 'Apple', email: 'email', phone: 'your mobile number',
  };
  const names = (providers || []).map((p) => labels[p]).filter(Boolean);
  if (!names.length) return 'your usual method';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}
